import {
  CONVERSATION_HISTORY_ENDPOINT,
  CONVERSATIONS_LIST_ENDPOINT,
} from "../constant/api-endpoints";
import {
  ConversationHistoryResponse,
  ConversationsListResponse,
} from "../taskpane/types/Conversation";
import {
  ForbiddenError,
  NotFoundError,
  ServiceUnavailableError,
  ConversationError,
} from "../taskpane/types/ConversationErrors";

export class ConversationHistoryService {
  private static async handleApiError(response: Response): Promise<never> {
    const status = response.status;
    let detail = "Une erreur est survenue";

    try {
      const data = await response.json();
      detail = data.detail || detail;
    } catch (e) {
      // Fallback if response is not JSON
    }

    if (status === 403) {
      throw new ForbiddenError(detail);
    } else if (status === 404) {
      throw new NotFoundError(detail);
    } else if (status === 503) {
      throw new ServiceUnavailableError(detail);
    } else {
      throw new ConversationError(detail, status);
    }
  }

  private static async fetchWithRetry(
    url: string,
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>,
    options: RequestInit = {},
    retries = 3,
    delay = 1000
  ): Promise<Response> {
    for (let i = 0; i < retries; i++) {
      const response = await authenticatedFetch(url, options);
      if (response.status === 503 && i < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
        continue;
      }
      return response;
    }
    return authenticatedFetch(url, options);
  }

  public static async getHistory(
    conversationId: string,
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
  ): Promise<ConversationHistoryResponse> {
    const url = `${CONVERSATION_HISTORY_ENDPOINT}/${conversationId}`;
    const response = await this.fetchWithRetry(url, authenticatedFetch);

    if (!response.ok) {
      await this.handleApiError(response);
    }

    const json = (await response.json()) as ConversationHistoryResponse;

    return json;
  }

  public static async listConversations(
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>,
    limit = 20,
    offset = 0
  ): Promise<ConversationsListResponse> {
    const url = `${CONVERSATIONS_LIST_ENDPOINT}?limit=${limit}&offset=${offset}`;
    const response = await this.fetchWithRetry(url, authenticatedFetch);

    if (!response.ok) {
      await this.handleApiError(response);
    }

    const json = (await response.json()) as ConversationsListResponse;

    return json;
  }
}
